import { useState } from "react";
import { AlertTriangle, Info, Play, Plus, Trash2 } from "lucide-react";
import {
  SEGMENT_OPTIONS,
} from "../benchmarkConstants";
import {
  ConfigTooltip,
  EarlyProvisionInfoModal,
  EarlyTerminationBanner,
  ModeToggle,
} from "./shared";
import type { BenchmarkCase, CaseDefinition } from "../types";

interface CustomCasesViewProps {
  initialCases?: BenchmarkCase[];
  maxConcurrent: number;
  maxMessages: number;
  shouldTerminateEarly: boolean;
  isCustomMode: boolean;
  situationOptions: readonly string[];
  suggestionOptions: readonly string[];
  onToggleMode: () => void;
  onSetMaxConcurrent: (n: number) => void;
  onSetMaxMessages: (max: number) => void;
  onSetShouldTerminateEarly: (v: boolean) => void;
  onRun: (cases: BenchmarkCase[]) => void;
}

function createEmptyDefinition(situationOptions: readonly string[]): CaseDefinition {
  return {
    situation: situationOptions[0] ?? "",
    segment: SEGMENT_OPTIONS[0],
    situationSummary: "",
    expectedSuggestion: "",
    personaDetail: "",
  };
}

function createEmptyCase(id: number, situationOptions: readonly string[]): BenchmarkCase {
  const def = createEmptyDefinition(situationOptions);
  return { id, originalCase: def, modifiedCase: { ...def }, comments: "" };
}

let nextCustomId = -1;

function allocateId(): number {
  return nextCustomId--;
}

export function CustomCasesView({
  initialCases,
  maxConcurrent,
  maxMessages,
  shouldTerminateEarly,
  isCustomMode,
  situationOptions,
  suggestionOptions,
  onToggleMode,
  onSetMaxConcurrent,
  onSetMaxMessages,
  onSetShouldTerminateEarly,
  onRun,
}: CustomCasesViewProps) {
  const [cases, setCases] = useState<BenchmarkCase[]>(() => {
    if (initialCases && initialCases.length > 0) {
      return initialCases.map((c) => ({ ...c, id: allocateId() }));
    }
    return [createEmptyCase(allocateId(), situationOptions)];
  });
  const [showEarlyProvisionInfo, setShowEarlyProvisionInfo] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Map<number, string[]>
  >(new Map());

  const updateCase = (id: number, field: keyof CaseDefinition, value: string) => {
    setCases((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              modifiedCase: { ...c.modifiedCase, [field]: value },
              originalCase: { ...c.originalCase, [field]: value },
            }
          : c,
      ),
    );
    setValidationErrors((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const addCase = () => {
    setCases((prev) => [...prev, createEmptyCase(allocateId(), situationOptions)]);
  };

  const removeCase = (id: number) => {
    setCases((prev) => prev.filter((c) => c.id !== id));
  };

  const validate = (): boolean => {
    const errors = new Map<number, string[]>();
    for (const c of cases) {
      const caseErrors: string[] = [];
      if (!c.modifiedCase.situationSummary.trim()) {
        caseErrors.push("Situation Summary is required");
      }
      if (!c.modifiedCase.personaDetail.trim()) {
        caseErrors.push("Persona Details is required");
      }
      if (caseErrors.length > 0) errors.set(c.id, caseErrors);
    }
    setValidationErrors(errors);
    return errors.size === 0;
  };

  const handleRun = () => {
    if (cases.length === 0 || !validate()) return;
    onRun(cases);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-brand-foreground">
          Evaluation
        </h1>
        <ModeToggle isCustomMode={isCustomMode} onToggle={onToggleMode} />
      </div>

      <div className="mb-4 flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          All information required to meet the desired segment, situation, and
          suggestion must be <strong>explicitly stated</strong> in the Situation
          Summary and Persona Details fields. The simulated user agent may
          provide different answers to questions than what you would expect if
          the relevant details are not included.
        </span>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <span className="text-sm text-brand-muted">
            {cases.length} custom case{cases.length !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-brand-muted whitespace-nowrap flex items-center gap-1">
              Max Concurrent
              <ConfigTooltip text="The number of evaluation cases to run in parallel. Higher values complete faster but use more resources." />
            </label>
            <input
              type="number"
              min={1}
              max={10}
              value={maxConcurrent}
              onChange={(e) =>
                onSetMaxConcurrent(
                  Math.max(1, Math.min(10, Number(e.target.value))),
                )
              }
              className="w-16 px-2 py-1 text-xs rounded-md border border-brand-border bg-brand-card text-brand-foreground text-center"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-brand-muted whitespace-nowrap flex items-center gap-1">
              Max Messages
              <ConfigTooltip text="The maximum number of messages (user + assistant) allowed per evaluation case before it is marked as abandoned." />
            </label>
            <input
              type="number"
              min={2}
              max={200}
              value={maxMessages}
              onChange={(e) =>
                onSetMaxMessages(Math.max(2, Number(e.target.value)))
              }
              className="w-16 px-2 py-1 text-xs rounded-md border border-brand-border bg-brand-card text-brand-foreground text-center"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-brand-muted whitespace-nowrap flex items-center gap-1">
              Early Product Recommendation Provision
              <button
                onClick={() => setShowEarlyProvisionInfo(true)}
                className="inline-flex p-0.5 rounded hover:bg-brand-primary/10 transition-colors"
              >
                <Info className="w-3.5 h-3.5 text-brand-muted/60 hover:text-brand-foreground cursor-pointer" />
              </button>
            </label>
            <select
              value={shouldTerminateEarly ? "yes" : "no"}
              onChange={(e) =>
                onSetShouldTerminateEarly(e.target.value === "yes")
              }
              className="px-2 py-1 text-xs rounded-md border border-brand-border bg-brand-card text-brand-foreground"
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={addCase}
            className="flex items-center gap-2 px-4 py-2 border border-brand-border text-brand-foreground rounded-md hover:bg-brand-primary/5 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Case
          </button>
          <button
            onClick={handleRun}
            disabled={cases.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="w-4 h-4" />
            Run Evaluation
          </button>
        </div>
      </div>

      {shouldTerminateEarly && <EarlyTerminationBanner />}

      <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
        {cases.map((c, index) => {
          const errors = validationErrors.get(c.id);
          return (
            <div
              key={c.id}
              className="bg-brand-card border border-brand-border rounded-lg p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-brand-foreground">
                  Case {index + 1}
                </h3>
                {cases.length > 1 && (
                  <button
                    onClick={() => removeCase(c.id)}
                    className="p-1.5 rounded hover:bg-red-50 text-brand-muted hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {errors && errors.length > 0 && (
                <div className="mb-3 text-xs text-red-600 space-y-0.5">
                  {errors.map((err) => (
                    <p key={err}>{err}</p>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-brand-muted mb-1">
                    Situation
                  </label>
                  <select
                    value={c.modifiedCase.situation}
                    onChange={(e) =>
                      updateCase(c.id, "situation", e.target.value)
                    }
                    className="w-full px-2 py-1.5 text-sm rounded-md border border-brand-border bg-brand-card text-brand-foreground"
                  >
                    {situationOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-brand-muted mb-1">
                    Segment
                  </label>
                  <select
                    value={c.modifiedCase.segment}
                    onChange={(e) =>
                      updateCase(c.id, "segment", e.target.value)
                    }
                    className="w-full px-2 py-1.5 text-sm rounded-md border border-brand-border bg-brand-card text-brand-foreground"
                  >
                    <option value="">None</option>
                    {SEGMENT_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-brand-muted mb-1">
                    Expected Suggestion
                  </label>
                  <select
                    value={c.modifiedCase.expectedSuggestion}
                    onChange={(e) =>
                      updateCase(c.id, "expectedSuggestion", e.target.value)
                    }
                    className="w-full px-2 py-1.5 text-sm rounded-md border border-brand-border bg-brand-card text-brand-foreground"
                  >
                    <option value="">None (expects no recommendation)</option>
                    {suggestionOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-brand-muted mb-1">
                    Situation Summary
                  </label>
                  <textarea
                    value={c.modifiedCase.situationSummary}
                    onChange={(e) =>
                      updateCase(c.id, "situationSummary", e.target.value)
                    }
                    rows={4}
                    placeholder="Describe the customer's financial situation..."
                    className="w-full px-3 py-2 text-sm rounded-md border border-brand-border bg-brand-card text-brand-foreground placeholder:text-brand-muted/50 resize-y"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-brand-muted mb-1">
                    Persona Details
                  </label>
                  <textarea
                    value={c.modifiedCase.personaDetail}
                    onChange={(e) =>
                      updateCase(c.id, "personaDetail", e.target.value)
                    }
                    rows={4}
                    placeholder="Describe the persona (age, circumstances, preferences)..."
                    className="w-full px-3 py-2 text-sm rounded-md border border-brand-border bg-brand-card text-brand-foreground placeholder:text-brand-muted/50 resize-y"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {cases.length === 0 && (
        <div className="text-center py-16 text-brand-muted">
          <p className="text-lg">No cases defined</p>
          <p className="text-sm mt-1">
            Add at least one custom case to run an evaluation
          </p>
          <button
            onClick={addCase}
            className="mt-4 flex items-center gap-2 px-4 py-2 mx-auto border border-brand-border text-brand-foreground rounded-md hover:bg-brand-primary/5 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Case
          </button>
        </div>
      )}

      {showEarlyProvisionInfo && (
        <EarlyProvisionInfoModal
          onClose={() => setShowEarlyProvisionInfo(false)}
        />
      )}
    </div>
  );
}
